import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import AdminAPI from "../../../services/AdminAPI";
import PlanReviewDrawer from "./PlanReviewDrawer";
import { FILTERS, classLabel, formatWhen, metaFor, timeAgo } from "./approvalMeta";

const PAGE_SIZE = 25;
const GRADES = ["BV1", "BV2", "BV3", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Toast({ toast, onClose }) {
  if (!toast) return null;
  const ok = toast.type === "success";
  return (
    <div
      role="status"
      className={`fixed right-4 top-20 z-[60] flex max-w-sm items-start gap-3 rounded-xl border px-4 py-3 text-[13px] shadow-lg ${
        ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-rose-200 bg-rose-50 text-rose-900"
      }`}
    >
      {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span className="whitespace-pre-wrap">{toast.msg}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss" className="ml-1 shrink-0 opacity-60 hover:opacity-100">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function Kpi({ label, value, tone, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:shadow ${
        active ? "border-[#1565c0] ring-2 ring-[#1565c0]/15" : "border-[#e6edf8]"
      }`}
    >
      <div className="text-[11px] font-medium text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${tone}`}>{value}</div>
    </button>
  );
}

function ProgressBar({ done, total }) {
  const pct = total ? Math.round((100 * done) / total) : 0;
  return (
    <div className="flex items-center gap-2" title={`${done} of ${total} chapters complete`}>
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-[#1565c0]"}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] tabular-nums text-slate-500">
        {done}/{total}
      </span>
    </div>
  );
}

function BulkConfirm({ rows, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Confirm bulk approval">
      <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Cancel" onClick={() => !busy && onCancel()} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <h3 className="m-0 text-[16px] font-bold text-slate-900">
          Approve {rows.length} plan{rows.length === 1 ? "" : "s"}?
        </h3>
        <p className="mt-1 text-[13px] text-slate-600">
          Each plan is checked again as it is approved. Any plan that was edited after you loaded this list is skipped and reported.
        </p>
        <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-[#e6edf8] bg-[#f9fbff] p-2 text-[12px]">
          {rows.map((r) => (
            <li key={r.uid} className="flex justify-between gap-3 rounded-lg bg-white px-2.5 py-1.5">
              <span className="font-medium text-slate-800">
                Grade {classLabel(r)} · {r.subject}
              </span>
              <span className="truncate text-slate-500">{r.teacher_name}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={onCancel} className="rounded-xl border border-slate-300 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Approve {rows.length}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ACPPApprovals() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [grade, setGrade] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], counts: {}, total: 0, role: "", academic_year: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [openUid, setOpenUid] = useState(params.get("uid") || "");
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [toast, setToast] = useState(null);
  const requestId = useRef(0);
  const debouncedSearch = useDebounced(search);

  const toastTimer = useRef(null);
  const notify = useCallback((type, msg) => {
    setToast({ type, msg });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const res = await AdminAPI.acppM6Queue({
        stage: filter,
        search: debouncedSearch || undefined,
        grade: grade || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      if (id !== requestId.current) return;
      setData(res);
    } catch (e) {
      if (id === requestId.current) setError(e.message || "Could not load plans.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [filter, debouncedSearch, grade, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to the first page / clear selection whenever the result set changes shape.
  useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch, grade]);
  useEffect(() => {
    setSelected(new Set());
  }, [filter, debouncedSearch, grade, page]);

  const items = useMemo(() => data.items || [], [data.items]);
  const approvable = useMemo(() => items.filter((r) => r.can_approve), [items]);
  const allSelected = approvable.length > 0 && approvable.every((r) => selected.has(r.uid));
  const selectedRows = useMemo(() => items.filter((r) => selected.has(r.uid)), [items, selected]);
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const openIndex = items.findIndex((r) => r.uid === openUid);

  const open = (uid) => {
    setOpenUid(uid);
    const next = new URLSearchParams(params);
    next.set("uid", uid);
    setParams(next, { replace: true });
  };
  const closeDrawer = useCallback(() => {
    setOpenUid("");
    const next = new URLSearchParams(params);
    next.delete("uid");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const toggle = (uid) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });

  const onDecided = async (res, mode) => {
    const label = items.find((r) => r.uid === openUid);
    const name = label ? `Grade ${classLabel(label)} ${label.subject}` : "Plan";
    const nextUid = openIndex >= 0 ? items[openIndex + 1]?.uid : "";
    notify(
      "success",
      mode === "override"
        ? `${name}: HOD step skipped. It is now waiting for your final approval.`
        : mode === "approve"
        ? `${name} ${res.workflow_stage === "approved" ? "approved" : "approved and sent to admin"}.${
            res.warnings?.length ? `\n${res.warnings.join("\n")}` : ""
          }`
        : `${name} sent back with your comments.`
    );
    await load();
    if (nextUid) open(nextUid);
    else closeDrawer();
  };

  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const res = await AdminAPI.acppM6BulkApprove({
        items: selectedRows.map((r) => ({ uid: r.uid, expected_revision: r.revision })),
      });
      const failed = (res.results || []).filter((r) => !r.ok);
      setBulkResult({ ...res, failed });
      notify(
        failed.length ? "error" : "success",
        `${res.succeeded} approved${failed.length ? `, ${failed.length} skipped` : ""}.`
      );
      setSelected(new Set());
      setConfirmBulk(false);
      await load();
    } catch (e) {
      notify("error", e.message || "Bulk approval failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const counts = data.counts || {};
  const byUid = useMemo(() => Object.fromEntries(items.map((r) => [r.uid, r])), [items]);

  return (
    <div className="min-h-screen bg-[#f4f8fe] pt-16 font-[poppins]">
      <div className="mx-auto w-full max-w-[1400px] px-4 pb-12 pt-4">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#D67C2C]">
              <ClipboardCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="m-0 text-[22px] font-bold text-[#1D3469]">Teacher Workflow Approvals</h1>
              <p className="m-0 text-[12px] text-slate-500">
                ACPP Teacher Workflow{data.academic_year ? ` · ${data.academic_year}` : ""}
                {data.role ? ` · reviewing as ${data.role === "management" ? "management" : data.role}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-xl border border-[#d9e7f7] bg-white px-3 py-2 text-[13px] font-medium text-[#1565c0] hover:bg-[#f4f8fe]"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </header>

        <section className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5" aria-label="Summary">
          <Kpi label="Needs my review" value={counts.pending ?? 0} tone="text-[#1565c0]" active={filter === "pending"} onClick={() => setFilter("pending")} />
          <Kpi label="Awaiting HOD" value={counts.hod_review ?? 0} tone="text-amber-600" active={filter === "hod_review"} onClick={() => setFilter("hod_review")} />
          <Kpi label="Awaiting Admin" value={counts.admin_review ?? 0} tone="text-blue-600" active={filter === "admin_review"} onClick={() => setFilter("admin_review")} />
          <Kpi label="Changes requested" value={counts.changes_requested ?? 0} tone="text-rose-600" active={filter === "changes_requested"} onClick={() => setFilter("changes_requested")} />
          <Kpi label="Approved" value={counts.approved ?? 0} tone="text-emerald-600" active={filter === "approved"} onClick={() => setFilter("approved")} />
        </section>

        <section className="rounded-2xl border border-[#e6edf8] bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-3 border-b border-[#e6edf8] p-3">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Status filter">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition ${
                    filter === f.key
                      ? "border-[#1565c0] bg-[#1565c0] text-white"
                      : "border-[#d9e7f7] bg-white text-slate-600 hover:bg-[#f4f8fe]"
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 ${filter === f.key ? "text-white/80" : "text-slate-400"}`}>{counts[f.countKey] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                aria-label="Filter by grade"
                className="rounded-xl border border-slate-300 bg-white px-2.5 py-2 text-[13px] text-slate-700"
              >
                <option value="">All grades</option>
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    Grade {g}
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search teacher, subject…"
                  aria-label="Search plans"
                  className="w-56 rounded-xl border border-slate-300 py-2 pl-8 pr-3 text-[13px] focus:border-[#1565c0] focus:outline-none focus:ring-2 focus:ring-[#1565c0]/20"
                />
              </div>
            </div>
          </div>

          {selected.size > 0 && (
            <div className="sticky top-16 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-2.5">
              <span className="text-[13px] font-medium text-emerald-900">
                {selected.size} plan{selected.size === 1 ? "" : "s"} selected
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelected(new Set())} className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-emerald-900 hover:bg-emerald-100">
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmBulk(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700"
                >
                  <CheckCircle2 className="h-4 w-4" /> Approve selected
                </button>
              </div>
            </div>
          )}

          {bulkResult?.failed?.length > 0 && (
            <div className="border-b border-rose-200 bg-rose-50 px-4 py-3 text-[12px] text-rose-900">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-semibold">
                  {bulkResult.succeeded} approved, {bulkResult.failed.length} skipped
                </span>
                <button type="button" onClick={() => setBulkResult(null)} aria-label="Dismiss" className="opacity-60 hover:opacity-100">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <ul className="m-0 list-disc pl-5">
                {bulkResult.failed.map((f) => (
                  <li key={f.uid}>
                    {byUid[f.uid] ? `Grade ${classLabel(byUid[f.uid])} ${byUid[f.uid].subject}` : f.uid}: {f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-left">
              <thead>
                <tr className="bg-[#f9fbff] text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all approvable plans on this page"
                      checked={allSelected}
                      disabled={approvable.length === 0}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(approvable.map((r) => r.uid)))}
                    />
                  </th>
                  <th className="px-3 py-3 font-semibold">Class</th>
                  <th className="px-3 py-3 font-semibold">Subject</th>
                  <th className="px-3 py-3 font-semibold">Teacher</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold">Progress</th>
                  <th className="px-3 py-3 font-semibold">Submitted</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const meta = metaFor(r.display_status, r.workflow_stage);
                  return (
                    <tr
                      key={r.uid}
                      className="cursor-pointer border-t border-[#eef3fa] text-[13px] hover:bg-[#f4f8fe]"
                      onClick={() => open(r.uid)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select Grade ${classLabel(r)} ${r.subject}`}
                          checked={selected.has(r.uid)}
                          disabled={!r.can_approve}
                          title={r.can_approve ? "" : r.is_own_plan ? "You cannot review your own plan" : "Not awaiting your review"}
                          onChange={() => toggle(r.uid)}
                        />
                      </td>
                      <td className="px-3 py-3 font-semibold text-slate-800">{classLabel(r)}</td>
                      <td className="px-3 py-3 text-slate-700">{r.subject}</td>
                      <td className="px-3 py-3 text-slate-700">
                        {r.teacher_name || <span className="text-slate-400">Unassigned</span>}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${meta.pill}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                        {r.hod_skipped_by && (
                          <span className="ml-1.5 text-[11px] font-medium text-amber-700" title={`HOD approval skipped by ${r.hod_skipped_by}`}>
                            HOD skipped
                          </span>
                        )}
                        {r.changed_since_approved && (
                          <span className="ml-1.5 text-[11px] font-medium text-amber-700" title="Content differs from the last approved version">
                            edited
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <ProgressBar done={r.chapters_complete} total={r.chapters} />
                      </td>
                      <td className="px-3 py-3 text-[12px] text-slate-500" title={formatWhen(r.submitted_at)}>
                        {r.submitted_at ? timeAgo(r.submitted_at) : "—"}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="text-[12px] font-semibold text-[#1565c0]">{r.can_approve || r.can_override_hod ? "Review" : "View"}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {loading && items.length === 0 && (
              <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
              </div>
            )}
            {!loading && error && (
              <div className="m-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-800">{error}</div>
            )}
            {!loading && !error && items.length === 0 && (
              <div className="py-16 text-center text-[13px] text-slate-500">
                {filter === "pending" ? "Nothing is waiting for your review. 🎉" : "No plans match these filters."}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-[#e6edf8] px-4 py-3 text-[12px] text-slate-500">
            <span>
              {data.total || 0} plan{data.total === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700 disabled:opacity-40"
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </div>

      {openUid && (
        <PlanReviewDrawer
          key={openUid}
          uid={openUid}
          academicYear={data.academic_year}
          role={data.role}
          onClose={closeDrawer}
          onDecided={onDecided}
          position={openIndex >= 0 ? `${openIndex + 1} of ${items.length}` : ""}
          onPrev={openIndex > 0 ? () => open(items[openIndex - 1].uid) : null}
          onNext={openIndex >= 0 && openIndex < items.length - 1 ? () => open(items[openIndex + 1].uid) : null}
        />
      )}
      {confirmBulk && (
        <BulkConfirm rows={selectedRows} busy={bulkBusy} onCancel={() => setConfirmBulk(false)} onConfirm={runBulk} />
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
